import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  GlobalButton,
  GlobalTextField,
  GlobalSelectField,
} from "../../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../global/GlobalModal";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import GlobalBadge from "../../../../global/GlobalBadge";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import GlobalPagination from "../../../../global/GlobalPagination";
import {
  fetchMaintenanceTypesMasterList,
  createMaintenanceTypeMaster,
  updateMaintenanceTypeMaster,
  deleteMaintenanceTypeMaster,
} from "../../api/maintenanceApi";
import { useMaintenanceRole } from "../../utils/useMaintenanceRole";
import { MAINTENANCE_RECORDS_CONTROL_SUBMODULE_SLUG } from "../../utils/maintenancePermissionSlugs";

const STATUS_OPTIONS = [
  { value: "1", label: "Active" },
  { value: "2", label: "Inactive" },
];

const MaintenanceTypeMasterManagement = ({ onNotify, onMastersChange }) => {
  const perms = useMaintenanceRole(MAINTENANCE_RECORDS_CONTROL_SUBMODULE_SLUG);
  const canManage = !perms.permissionsLoading && perms.canManageMasters;
  const canDelete = !perms.permissionsLoading && perms.canDeleteMasters;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const [formData, setFormData] = useState({
    id: null,
    type_name: "",
    status_id: 1,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const formSectionRef = useRef(null);
  const alertRef = useRef(null);

  const ALERT_ID = "md-type-master-alert";

  const scrollToMasterForm = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  const fetchRows = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await fetchMaintenanceTypesMasterList({
          page,
          limit: pagination.pageSize,
        });
        const payload = res?.data ?? res;
        const list = payload?.types ?? [];
        const pg = payload?.pagination ?? {};
        setRows(Array.isArray(list) ? list : []);
        setPagination((prev) => ({
          ...prev,
          total: Number(pg.total ?? 0),
          totalPages: Math.max(1, Number(pg.totalPages ?? 1)),
          page: Number(pg.page ?? page),
        }));
      } catch (error) {
        onNotify?.(error.message, { variant: "error" });
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [onNotify, pagination.pageSize],
  );

  useEffect(() => {
    fetchRows(1);
  }, [fetchRows]);

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setFormError(null);

    const name = String(formData.type_name || "").trim();
    if (!name) {
      setFormError("Type name is required.");
      setTimeout(() => alertRef.current?.focus(), 0);
      return;
    }

    const statusId = Number(formData.status_id) === 2 ? 2 : 1;

    try {
      if (isEditing && formData.id) {
        await updateMaintenanceTypeMaster(formData.id, {
          type_name: name,
          status_id: statusId,
        });
        onNotify?.("Maintenance type updated successfully.", { variant: "success" });
      } else {
        await createMaintenanceTypeMaster({
          type_name: name,
          status_id: statusId,
        });
        onNotify?.("Maintenance type created successfully.", { variant: "success" });
      }
      setFormData({ id: null, type_name: "", status_id: 1 });
      setIsEditing(false);
      setFormError(null);
      await fetchRows(pagination.page);
      onMastersChange?.();
    } catch (error) {
      const msg = error?.message || "Save failed";
      setFormError(msg);
      onNotify?.(msg, { variant: "error" });
      setTimeout(() => alertRef.current?.focus(), 0);
    }
  };

  const handleCancel = () => {
    setFormData({ id: null, type_name: "", status_id: 1 });
    setFormError(null);
    setIsEditing(false);
  };

  const handleEdit = (row) => {
    setFormData({
      id: row.id,
      type_name: row.type_name || "",
      status_id: Number(row.status_id) === 2 ? 2 : 1,
    });
    setIsEditing(true);
    scrollToMasterForm();
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await deleteMaintenanceTypeMaster(itemToDelete.id);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      onNotify?.("Maintenance type removed successfully.", { variant: "success" });
      await fetchRows(pagination.page);
      onMastersChange?.();
    } catch (error) {
      onNotify?.(error.message, { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="ub-master-section">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.25rem",
          flexWrap: "wrap",
          gap: "0.75rem",
        }}
      >
        <h3 className="ub-section__subhead" style={{ margin: 0 }}>
          Maintenance type master
        </h3>
      </div>

      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        {canManage ? (
          <GlobalFormShell
          legend={isEditing ? "Edit maintenance type" : "Add maintenance type"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {isEditing ? (
                <GlobalButton variant="ghost" onClick={handleCancel}>
                  Cancel edit
                </GlobalButton>
              ) : null}
              <GlobalButton variant="primary" onClick={handleSubmit}>
                {isEditing ? "Save type" : "Create type"}
              </GlobalButton>
            </>
          }
        >
          <GlobalTextField
            id={isEditing ? "maint_type_name_edit" : "maint_type_name"}
            label="Type name"
            value={formData.type_name}
            onChange={(v) => setFormField("type_name", v)}
            required
          />
          <GlobalSelectField
            id={isEditing ? "maint_type_status_edit" : "maint_type_status"}
            label="Status"
            value={String(formData.status_id)}
            onChange={(v) => setFormField("status_id", Number(v) === 2 ? 2 : 1)}
            options={STATUS_OPTIONS}
          />
        </GlobalFormShell>
        ) : (
          <p className="visitor-workspace__placeholder" style={{ marginBottom: "1rem" }}>
            You have view-only access to maintenance types.
          </p>
        )}
      </div>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Maintenance type master table"
        headers={[
          { key: "type_name", label: "Type name" },
          { key: "status", label: "Status" },
          { key: "created_at", label: "Created date" },
          {
            key: "actions",
            label: "Actions",
            className: "ub-table__col-actions",
          },
        ]}
      >
        {loading ? (
          <tr>
            <td colSpan={4} style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>
              Loading…
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr key={row.id}>
              <td style={{ fontWeight: 500 }}>{row.type_name}</td>
              <td>
                <GlobalBadge active={Number(row.status_id) === 1} />
              </td>
              <td>
                {row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}
              </td>
              <td className="ub-table__col-actions">
                {canManage ? (
                  <div className="ub-table-actions">
                    <GlobalTableActionButton action="edit" onClick={() => handleEdit(row)} />
                    {canDelete ? (
                      <GlobalTableActionButton
                        action="delete"
                        onClick={() => handleDeleteClick(row)}
                      />
                    ) : null}
                  </div>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))
        )}
        {!loading && rows.length === 0 ? (
          <tr>
            <td colSpan={4} style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>
              No maintenance types found.
            </td>
          </tr>
        ) : null}
      </GlobalDataTable>

      {pagination.totalPages > 1 ? (
        <div style={{ marginTop: "1rem" }}>
          <GlobalPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={(p) => fetchRows(p)}
          />
        </div>
      ) : null}

      <GlobalModal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        title="Confirm removal"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
              Cancel
            </GlobalButton>
            <GlobalButton variant="danger" onClick={handleConfirmDelete} loading={isDeleting}>
              Remove
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove maintenance type <b>{itemToDelete?.type_name}</b>? It will be marked inactive
          (is_active) and hidden from lists.
        </p>
      </GlobalModal>
    </div>
  );
};

export default MaintenanceTypeMasterManagement;
